import { useEffect, useState } from "react";
import { llmApi } from "../api/client";
import type { LLMSettings, ModelsByProvider, ModelFeature, FeatureOverride, SettingsMode } from "../types";
import ModelSelect from "../components/ModelSelect";
import SearchThreadEditor from "../components/SearchThreadEditor";
import { loadAutoMemoryExtraction, saveAutoMemoryExtraction } from "../lib/memoryPrefs";
import { loadSkipToolConfirmations, saveSkipToolConfirmations } from "../lib/toolPrefs";
import "./SettingsView.css";

// F10 fix: defaultModel/visionModel/autoTitle/streamTokens used to be dead
// localStorage writes nothing ever read (research_service.py etc run
// server-side with no browser attached). They now live in llm_settings
// (Task 4.4). Only theme stays client-side — it must apply before first
// paint and is genuinely per-device.
const THEME_KEY = "chatclient.theme";

function loadTheme(): string {
  return localStorage.getItem(THEME_KEY) || "dark";
}

function saveTheme(t: string) {
  localStorage.setItem(THEME_KEY, t);
}

const FEATURE_LABELS: Record<ModelFeature, string> = {
  chat: "Chat", synthesis: "Synthesis (answers)", rewrite: "Query rewrite",
  classify: "Intent classify", related: "Related questions", title: "Chat titles",
  research: "Autoresearch", vision: "Vision", memory: "Memory extraction",
  embedding: "Embedding", wiki: "Wiki generation",
  search_intent: "Search intent",
};

export default function SettingsView() {
  const [catalog, setCatalog] = useState<ModelsByProvider>({});
  const [settings, setSettings] = useState<LLMSettings | null>(null);
  const [theme, setTheme] = useState(loadTheme());
  const [refreshing, setRefreshing] = useState(false);
  const [autoMemoryExtraction, setAutoMemoryExtraction] = useState(loadAutoMemoryExtraction());
  const [skipToolConfirmations, setSkipToolConfirmations] = useState(loadSkipToolConfirmations());
  const [modeTab, setModeTab] = useState<SettingsMode>("chat");

  useEffect(() => {
    // Task 4.8 — mount calls models(false); Refresh Models is the only
    // caller of models(true), never polled.
    llmApi.models(false).then(setCatalog).catch(() => {});
    llmApi.settings().then(setSettings).catch(() => {});
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const refreshModels = async () => {
    setRefreshing(true);
    try {
      setCatalog(await llmApi.models(true));
    } finally {
      setRefreshing(false);
    }
  };

  const saveGlobal = async (patch: Partial<LLMSettings>) => {
    if (!settings) return;
    const next = { ...settings, ...patch };
    setSettings(next);
    await llmApi.saveSettings(patch);
  };

  const saveOverride = async (feature: ModelFeature, patch: FeatureOverride | null) => {
    if (!settings) return;
    const overrides = { ...settings.overrides };
    if (patch === null) delete overrides[feature];
    else overrides[feature] = { ...overrides[feature], ...patch };
    const next = { ...settings, overrides };
    setSettings(next);
    await llmApi.saveSettings({ overrides });
  };

  // Per-mode overrides (redesign: Chat and Ask each get their own
  // independent overrides blob, chat_overrides/ask_overrides).
  const saveModeOverride = async (mode: SettingsMode, feature: ModelFeature, patch: FeatureOverride | null) => {
    if (!settings) return;
    const key = mode === "chat" ? "chat_overrides" : "ask_overrides";
    const overrides = { ...settings[key] };
    if (patch === null) delete overrides[feature];
    else overrides[feature] = { ...overrides[feature], ...patch };
    const next = { ...settings, [key]: overrides };
    setSettings(next);
    await llmApi.saveSettings({ [key]: overrides });
  };

  const changeTheme = (t: string) => {
    setTheme(t);
    saveTheme(t);
  };

  const changeAutoMemoryExtraction = (v: boolean) => {
    setAutoMemoryExtraction(v);
    saveAutoMemoryExtraction(v);
  };

  const changeSkipToolConfirmations = (v: boolean) => {
    setSkipToolConfirmations(v);
    saveSkipToolConfirmations(v);
  };

  const globalFeatures: ModelFeature[] = ["embedding", "wiki"];
  const chatFeatures: ModelFeature[] = settings?.chat_features || ["chat", "vision", "title", "memory", "research", "search_intent"];
  const askFeatures: ModelFeature[] = settings?.ask_features || ["rewrite", "search_intent", "synthesis", "related"];
  const modeFeatures = modeTab === "chat" ? chatFeatures : askFeatures;
  const modeOverrides = settings ? (modeTab === "chat" ? settings.chat_overrides : settings.ask_overrides) : {};

  const filterFor = (feature: ModelFeature) => {
    if (feature === "chat" || feature === "research") {
      // §1.1.1 — Google 400s on tool turn 2. Restrict the picker to
      // providers whose derived supports.tool_loop is true.
      return (p: string, supports: Record<string, boolean>) => !!supports.tool_loop || p === "ollama-direct";
    }
    if (feature === "vision") {
      // Anthropic 400s on the standard vision route (§1.1.2).
      return (p: string) => p === "openai" || p === "google" || p === "ollama-direct";
    }
    return undefined;
  };

  return (
    <div className="view">
      <div className="view-header">
        <div>
          <h2 className="view-title">⚙️ Settings</h2>
          <p className="view-subtitle">Model selection and preferences</p>
        </div>
      </div>

      <div className="card settings-card">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h3 className="settings-section-title">AI Models</h3>
          <button className="btn btn-sm" onClick={refreshModels} disabled={refreshing}>
            {refreshing ? "Refreshing…" : "Refresh Models"}
          </button>
        </div>

        {settings ? (
          <>
            <div className="col">
              <label className="form-label">Global default</label>
              <ModelSelect
                catalog={catalog}
                provider={settings.provider}
                model={settings.model}
                onChange={(provider, model) => saveGlobal({ backend: "gateway", provider, model })}
              />
            </div>

            <hr className="settings-divider" />
            <h3 className="settings-section-title">Global overrides</h3>
            <div className="col">
              {globalFeatures.map((feature) => {
                const override = settings.overrides[feature];
                const enabled = !!override;
                return (
                  <div key={feature} className="settings-toggle-row">
                    <label className="row" style={{ gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={enabled}
                        disabled={feature === "embedding"}
                        onChange={(e) =>
                          saveOverride(
                            feature,
                            e.target.checked
                              ? { provider: settings.provider, model: settings.model }
                              : null
                          )
                        }
                      />
                      <span className="settings-toggle-name">{FEATURE_LABELS[feature]}</span>
                    </label>
                    {feature === "embedding" ? (
                      <span className="dim">
                        text-embedding-3-small · dim 1536 (read-only — changing it invalidates every stored vector)
                      </span>
                    ) : enabled ? (
                      <ModelSelect
                        catalog={catalog}
                        provider={override?.provider || null}
                        model={override?.model || ""}
                        onChange={(provider, model) => saveOverride(feature, { provider, model })}
                        allowedProviders={filterFor(feature)}
                      />
                    ) : (
                      <span className="dim">(uses global default)</span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="dim">Loading…</div>
        )}
      </div>

      <div className="card settings-card">
        <div className="tabs">
          <button
            className={modeTab === "chat" ? "tab active" : "tab"}
            onClick={() => setModeTab("chat")}
          >
            Chat
          </button>
          <button
            className={modeTab === "ask" ? "tab active" : "tab"}
            onClick={() => setModeTab("ask")}
          >
            Ask
          </button>
        </div>

        {settings ? (
          <>
            <h3 className="settings-section-title" style={{ marginTop: 12 }}>
              {modeTab === "chat" ? "Chat" : "Ask"} model overrides
            </h3>
            <div className="col">
              {modeFeatures.map((feature) => {
                const override = modeOverrides[feature];
                const enabled = !!override;
                return (
                  <div key={feature} className="settings-toggle-row">
                    <label className="row" style={{ gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) =>
                          saveModeOverride(
                            modeTab,
                            feature,
                            e.target.checked
                              ? { provider: settings.provider, model: settings.model }
                              : null
                          )
                        }
                      />
                      <span className="settings-toggle-name">{FEATURE_LABELS[feature]}</span>
                    </label>
                    {enabled ? (
                      <ModelSelect
                        catalog={catalog}
                        provider={override?.provider || null}
                        model={override?.model || ""}
                        onChange={(provider, model) => saveModeOverride(modeTab, feature, { provider, model })}
                        allowedProviders={filterFor(feature)}
                      />
                    ) : (
                      <span className="dim">(uses global default)</span>
                    )}
                    {((modeTab === "chat" && feature === "chat") || (modeTab === "ask" && feature === "synthesis")) && (
                      <div className="dim" style={{ fontSize: 12, marginTop: 2 }}>
                        Overridden per-conversation by the model picker in the chat composer
                        when set — this setting is only the fallback used otherwise.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <hr className="settings-divider" />
            <h3 className="settings-section-title">{modeTab === "chat" ? "Chat" : "Ask"} search threads</h3>
            <SearchThreadEditor mode={modeTab} />
          </>
        ) : (
          <div className="dim">Loading…</div>
        )}
      </div>

      <div className="card settings-card">
        <h3 className="settings-section-title">Preferences</h3>
        <div className="col">
          <label className="settings-toggle-row">
            <div>
              <div className="settings-toggle-name">Auto-generate chat titles</div>
              <div className="dim" style={{ fontSize: 12 }}>Generate a short title after the first message</div>
            </div>
            <input
              type="checkbox"
              checked={settings?.auto_title ?? true}
              onChange={(e) => saveGlobal({ auto_title: e.target.checked })}
            />
          </label>
          <label className="settings-toggle-row">
            <div>
              <div className="settings-toggle-name">Stream tokens</div>
              <div className="dim" style={{ fontSize: 12 }}>Stream responses token-by-token</div>
            </div>
            <input
              type="checkbox"
              checked={settings?.stream_tokens ?? true}
              onChange={(e) => saveGlobal({ stream_tokens: e.target.checked })}
            />
          </label>
          <label className="settings-toggle-row">
            <div>
              <div className="settings-toggle-name">Auto-save memories from chats</div>
              <div className="dim" style={{ fontSize: 12 }}>
                After each completed turn (while the chat's Memory toggle is on), extract
                and store memorable facts in the background. Stored on this device only.
              </div>
            </div>
            <input
              type="checkbox"
              checked={autoMemoryExtraction}
              onChange={(e) => changeAutoMemoryExtraction(e.target.checked)}
            />
          </label>
          <label className="settings-toggle-row">
            <div>
              <div className="settings-toggle-name">Skip tool confirmations</div>
              <div className="dim" style={{ fontSize: 12 }}>
                Run every tool call without asking, including MCP and plugin tools
                that normally pause after web content is read. Tool calls can then
                change external services without your approval. Off by default.
              </div>
            </div>
            <input
              type="checkbox"
              checked={skipToolConfirmations}
              onChange={(e) => changeSkipToolConfirmations(e.target.checked)}
            />
          </label>
          <div>
            <label className="form-label">Theme</label>
            <select className="select" value={theme} onChange={(e) => changeTheme(e.target.value)}>
              <option value="dark">Dark (rounded)</option>
              <option value="midnight">Midnight</option>
              <option value="daylight">Daylight</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
